import React, { useState, useEffect } from 'react';
import { FormField, Select } from '../../../components/ui/Form';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { useSettings } from '../../../hooks/useSettings';
import { AccountInitializationCard } from './AccountInitializationCard';
import { useAccountInitialization } from '../../../hooks/useAccountInitialization';

export function AccountingSettings() {
  const { settings, loading, updateSettings } = useSettings();
  const { checkInitializationStatus, currentFiscalYear } = useAccountInitialization();
  const [accountingFramework, setAccountingFramework] = useState('SYSCOHADA_NORMAL');
  const [defaultDepreciationMethod, setDefaultDepreciationMethod] = useState('linear');
  const [defaultVatRate, setDefaultVatRate] = useState(18);
  const [journalEntryValidation, setJournalEntryValidation] = useState('manual');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (settings?.accounting) {
      setAccountingFramework(settings.accounting.accountingFramework);
      setDefaultDepreciationMethod(settings.accounting.defaultDepreciationMethod);
      setDefaultVatRate(settings.accounting.defaultVatRate);
      setJournalEntryValidation(settings.accounting.journalEntryValidation);
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await updateSettings({
        category: 'accounting',
        settings: {
          ...settings?.accounting,
          accountingFramework,
          defaultDepreciationMethod,
          defaultVatRate,
          journalEntryValidation
        }
      });
      alert('Configuration enregistrée avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card title="Configuration comptable">
          <p className="text-center py-4">Chargement...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Carte d'initialisation des comptes */}
      <AccountInitializationCard 
        onInitialized={() => {
          // Rafraîchir le statut après initialisation
          if (currentFiscalYear?.id) {
            checkInitializationStatus(currentFiscalYear.id);
          }
        }}
      />

      <Card title="Configuration comptable">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-6">
            <FormField 
              label="Plan comptable par défaut" 
              hint="Sera utilisé comme valeur par défaut lors de l'initialisation d'un nouvel exercice fiscal"
            >
              <Select
                value={accountingFramework}
                onChange={(e) => setAccountingFramework(e.target.value)}
                options={[
                  { value: 'SYSCOHADA_NORMAL', label: 'SYSCOHADA Normal' },
                  { value: 'SYSCOHADA_MINIMAL', label: 'SYSCOHADA MINIMAL (SMT)' },
                  { value: 'IFRS', label: 'IFRS' }
                ]}
              />
            </FormField>

            <FormField label="Méthode d'amortissement par défaut">
              <Select
                value={defaultDepreciationMethod}
                onChange={(e) => setDefaultDepreciationMethod(e.target.value)}
                options={[
                  { value: 'linear', label: 'Linéaire' },
                  { value: 'degressive', label: 'Dégressif' }
                ]}
              />
            </FormField>

            <FormField label="TVA par défaut">
              <Select
                value={defaultVatRate.toString()}
                onChange={(e) => setDefaultVatRate(Number(e.target.value))}
                options={[
                  { value: '18', label: '18%' },
                  { value: '16', label: '16%' },
                  { value: '0', label: '0%' }
                ]}
              />
            </FormField>

            <FormField label="Validation des écritures">
              <Select
                value={journalEntryValidation}
                onChange={(e) => setJournalEntryValidation(e.target.value)}
                options={[
                  { value: 'auto', label: 'Automatique' },
                  { value: 'manual', label: 'Manuelle' }
                ]}
              />
            </FormField>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Enregistrement...' : 'Enregistrer la configuration'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}